'use client';

import {LoadingContextProvider} from "../../context/loading";
import {CssBaseline} from "@mui/material";
import {createTheme, ThemeProvider} from "@mui/material/styles";
import {nuinto} from "../../lib/fonts";
import {ComponentsProps} from "@mui/material/styles/props";
import {ComponentsOverrides} from "@mui/material/styles/overrides";
import {ComponentsVariants} from "@mui/material/styles/variants";
import {FileUploadProps} from "primereact/fileupload";
import {FileUploadClassKey} from "../../pages/components/FileUpload";
import {RecentActionProvider} from "./context/RecentActionContext";
import {CasesProvider} from "./context/CasesContext";
import {CaseChatsProvider} from "./context/CaseChatsContext";
import GlobalStyles from '@mui/material/GlobalStyles';
import gradient from "../../assets/bg_1920_1080.png"
import {Bounce, ToastContainer} from "react-toastify";
import {ApiClientMiddlewareLayerProvider} from "./context/ApiMiddlewareLayerContext";
import {ThemeContextProvider} from "./context/ThemeContext";

declare module '@mui/material/styles' {
    interface TypeText {
        lowEm: string;
        midEm: string;
        highEm: string;
    }

    interface ComponentsPropsList {
        FileUpload: FileUploadProps;
    }
    interface ComponentNameToClassKey {
        FileUpload: FileUploadClassKey;
    }

    interface Components<Theme = unknown> {
        FileUpload?: {
            defaultProps?: ComponentsProps['FileUpload'];
            styleOverrides?: ComponentsOverrides<Theme>['FileUpload'];
            variants?: ComponentsVariants<Theme>['FileUpload'];
        };
    }
}

const primaryMain = "#042039";
const primaryLight = "#0b2e50";
const primaryDark = "#021528";
const primaryContrastText = "#e4eef9";
const secondaryMain = "#80ade0";
const secondaryLight = "#a9c9e9";
const secondaryDark = "#3d7ba6";
const successMain = "#68D391";
const successDark = "#439264";

let theme = createTheme({
    cssVariables: true,
    typography: {
        fontFamily: "Nunito Sans, Arial"
    },

    components: {
        MuiToolbar: {
            styleOverrides: {
                regular: {
                    background: "#FFFFFF",
                }
            }
        },
        MuiCssBaseline: {
            styleOverrides: `
                @font-face {
                  font-family: 'Nunito Sans';
                  font-style: normal;
                  font-display: swap;
                  font-weight: 400;
                  src: local('Nunito Sans'), local('Nunito Sans'), url(${nuinto}) format('woff2');
                  unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
                }
            `,
        }
    },
    palette: {
        mode: 'dark',
        common: {
            black: "#000",
            white: "#fff",
        },
        primary: {
            main: primaryMain,
            light: primaryLight,
            dark: primaryDark,
            contrastText: primaryContrastText,
        },
        secondary: {
            main: secondaryMain,
            light: secondaryLight,
            dark: secondaryDark,
            contrastText: "rgba(0, 0, 0, 0.87)",
        },
        error: {

            main: "#f44336",
            light: "#e57373",
            dark: "#d32f2f",
            contrastText: "#fff",
        },
        warning: {
            main: "#ffa726",
            light: "#ffb74d",
            dark: "#f57c00",
            contrastText: "rgba(0, 0, 0, 0.87)",
        },
        info: {

            main: "#29b6f6",
            light: "#4fc3f7",
            dark: "#0288d1",
            contrastText: "rgba(0, 0, 0, 0.87)",
        },
        success: {

            main: successMain,
            light: "#81c784",
            dark: successDark,
            contrastText: "rgba(246, 236, 236, 0.87)",
        },
        grey: {

            50: "#fafafa",
            100: "#f5f5f5",
            200: "#eeeeee",
            300: "#e0e0e0",
            400: "#bdbdbd",
            500: "#9e9e9e",
            600: "#757575",
            700: "#616161",
            800: "#424242",
            900: "#212121",
            A100: "#f5f5f5",
            A200: "#eeeeee",
            A400: "#bdbdbd",
            A700: "#616161",
        },
        contrastThreshold: 3,
        tonalOffset: 0.2,
        text: {

            primary: "rgba(228, 238, 249, 0.87)",
            secondary: "rgba(212, 224, 228, 0.7)",
            disabled: " rgba(4, 32, 57, 0.7)",
        },
        divider: "#80ADE0",
        background: {

            paper: "#042039",
            default: primaryLight,
        },

        action: {

            active: "#fff",
            hover: "rgba(255, 255, 255, 0.08)",
            hoverOpacity: 0.08,
            selected: "rgba(255, 255, 255, 0.16)",
            selectedOpacity: 0.16,
            disabled: "rgba(255, 255, 255, 0.3)",
            disabledBackground: "rgba(255, 255, 255, 0.12)",
            disabledOpacity: 0.38,
            focus: "rgba(255, 255, 255, 0.12)",
            focusOpacity: 0.12,
            activatedOpacity: 0.24,
        },
    }
});

export function Providers({children}) {

    return (
        <ThemeContextProvider>
            <CssBaseline/>
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                limit={5}
                hideProgressBar
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
                transition={Bounce}/>
            <LoadingContextProvider>
                <ApiClientMiddlewareLayerProvider>
                    <RecentActionProvider>
                        <CasesProvider>
                            <CaseChatsProvider>
                                {children}
                            </CaseChatsProvider>
                        </CasesProvider>
                    </RecentActionProvider>
                </ApiClientMiddlewareLayerProvider>
            </LoadingContextProvider>
        </ThemeContextProvider>
    );
}